import { useEffect, useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { FaChevronDown, FaMapMarkerAlt, FaWhatsapp } from 'react-icons/fa';
import { BsInstagram } from 'react-icons/bs';
import { gql, useQuery } from '@apollo/client';

import Container from './App.style';
import LogoOfertou from '../../assets/logo-homepage.svg';
import WebAppIcon from '../../assets/web-app-icon.svg';
import CitySelect from '../../components/CitySelect';
import { dateFormat } from '../../utils/dateFormat';
import ProductCard from '../../components/ProductCartd';
import ModalProduct from '../../components/ModalProduct';
import { BodyModalProductSelected } from '../../components/BodyModalProductSelected';
import LoadingFullScreen from '../../components/LoadingFullScreen';
import { ListEmpty } from '../../components/ListEmpty';
import { Button } from '../../components/Button';
import PWAInstallButton from '../../components/PWAInstallButton';
import Footer from '../../components/Footer';
import MapView from '../../components/MapView';

import { getToken, onMessage } from 'firebase/messaging';
import { messaging } from '../../firebase/firebaseConfig';

import Message from '../../components/Notification';
import 'react-toastify/dist/ReactToastify.css';

const { VITE_APP_VAPID_KEY } = import.meta.env;

interface Location {
  latitude: number;
  longitude: number;
  timestamp: number;
}

const GET_PRODUCTS = gql`
  query GetProducts($today: Date!) {
    shops(where: { cityId: { eq: 1 } }) {
      name
      location
      shopLogo
      whatsapp
      instagram
      lat
      lon
      offers(where: { isActive: { eq: 1 }, endOffer: { gte: $today } }) {
        product {
          title
          image
        }
        startOffer
        endOffer
        oldPrice
        newPrice
        amount
      }
    }
  }
`;

export interface ProductAllInfo {
  shopName: string;
  location: string;
  productTitle: string;
  endOffer: string;
  newPrice: number;
  oldPrice: number;
  productImageUrl: string;
  whatsapp: string | null;
  instagram: string | null;
}

interface Offer {
  product: {
    title: string;
    image: string;
  };
  endOffer?: string;
  startOffer: string;
  oldPrice: number;
  newPrice: number;
  amount: number;
}

interface Shop {
  location: string;
  name: string;
  offers: Offer[];
  shopLogo: string;
  whatsapp: string | null;
  instagram: string | null;
  lat: number;
  lon: number;
}

export interface City {
  id: number;
  nome: string;
}

const LOCATION_EXPIRY = 36 * 60 * 60 * 1000; // 36 hours in milliseconds

const App = () => {
  const navigate = useNavigate();
  // Get today's date in YYYY-MM-DD format
  const today = new Date().toISOString().slice(0, 10);
  const { loading, error, data } = useQuery(GET_PRODUCTS, {
    variables: { today },
  });
  const [citySelected, setCitySelected] = useState('');
  const [openModalCities, setOpenModalCities] = useState(false);
  const [openModalSelectedProduct, setOpenModalSelectedProduct] =
    useState(false);
  const [productSelected, setProductSelected] = useState<ProductAllInfo>({
    shopName: '',
    location: '',
    productTitle: '',
    endOffer: '',
    productImageUrl: '',
    newPrice: 0,
    oldPrice: 0,
    whatsapp: '',
    instagram: '',
  });
  const [cities, setCities] = useState<City[]>([
    { id: 1, nome: 'Jacareí - SP' },
  ]);
  const [sortedShops, setSortedShops] = useState<any[]>([]);
  const [userLocation, setUserLocation] = useState<Location | null>(null);
  const [activeTab, setActiveTab] = useState<'offers' | 'partners' | 'mapa'>('offers');

  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const R = 6371; // Earth's radius in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const isLocationExpired = (timestamp: number) => {
    return Date.now() - timestamp > LOCATION_EXPIRY;
  };

  const getLocationFromStorage = () => {
    const storedLocation = localStorage.getItem('@ofertou:userLocation');
    if (storedLocation) {
      const location: Location = JSON.parse(storedLocation);
      if (!isLocationExpired(location.timestamp)) {
        return location;
      }
      // Clear expired location
      localStorage.removeItem('@ofertou:userLocation');
    }
    return null;
  };

  const sortShopsByDistance = (location: Location) => {
    if (data?.shops) {
      const sorted = [...data.shops].sort((a, b) => {
        const distanceA = calculateDistance(
          location.latitude,
          location.longitude,
          a.lat,
          a.lon
        );
        const distanceB = calculateDistance(
          location.latitude,
          location.longitude,
          b.lat,
          b.lon
        );
        return distanceA - distanceB;
      });

      // Log sorted shops with distances
      console.log('Shops sorted by distance from your location:');
      sorted.forEach((shop, index) => {
        const distance = calculateDistance(
          location.latitude,
          location.longitude,
          shop.lat,
          shop.lon
        );
        console.log(`${index + 1}. ${shop.name} - ${distance.toFixed(2)} km`);
      });

      setSortedShops(sorted);
    }
  };

  const handleSortByDistance = async () => {
    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });

      const location: Location = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        timestamp: Date.now()
      };

      // Store location in localStorage and state
      localStorage.setItem('@ofertou:userLocation', JSON.stringify(location));
      setUserLocation(location);
      sortShopsByDistance(location);
      
      // Only show toast notification if not in map tab
      if (activeTab !== 'mapa') {
        toast.success("Ordenado por proximidade com sucesso!");
      }
    } catch (error) {
      toast.error('Não foi possível obter sua localização. Verifique as permissões do navegador.');
    }
  };

  const handleOpenModalProductSelected = ({
    shopName,
    location,
    productTitle,
    endOffer,
    newPrice,
    oldPrice,
    productImageUrl,
    whatsapp,
    instagram,
  }: ProductAllInfo) => {
    setProductSelected({
      shopName: shopName,
      location: location,
      productTitle: productTitle,
      endOffer: endOffer,
      newPrice: newPrice,
      oldPrice: oldPrice,
      productImageUrl: productImageUrl,
      whatsapp: whatsapp,
      instagram: instagram,
    });
    setOpenModalSelectedProduct(true);
  };

  const handleSelect = async (city: string) => {
    setCitySelected(city);
    window.localStorage.setItem('@ofertou:cidade', city);
    setOpenModalCities(false);
  };

  const handleClickLogo = () => {
    navigate('/');
  };

  const changeTab = (tab: 'offers' | 'partners' | 'mapa') => {
    setActiveTab(tab);
  };

  // Get only shops with offers
  const shopsWithOffers = (sortedShops.length > 0 ? sortedShops : data?.shops || [])
    .filter((shop: Shop) => shop.offers.length > 0);

  // Get all shops for partner shops tab
  const allShops = sortedShops.length > 0 ? sortedShops : data?.shops || [];

  async function requestPermission() {
    //requesting permission using Notification API
    const permission = await Notification.requestPermission();

    if (permission === "granted") {
      const token = await getToken(messaging, {
        vapidKey: VITE_APP_VAPID_KEY,
      });
      localStorage.setItem('fcm_token', token);
      onMessage(messaging, (payload) => {
        toast(<Message notification={payload.notification} />);
      });
    }
  }

  useEffect(() => {
    const cityLocalStorage = window.localStorage.getItem('@ofertou:cidade');
    if (cityLocalStorage) {
      setCitySelected(cityLocalStorage);
    }

    // Load location from storage if available and not expired
    const storedLocation = getLocationFromStorage();
    if (storedLocation) {
      setUserLocation(storedLocation);
      sortShopsByDistance(storedLocation);
    }

    requestPermission();
  }, [data?.shops]);

  if (error) {
    return <ListEmpty message={error.message} />;
  }

  if (loading) {
    return <LoadingFullScreen />;
  }

  return (
    <div className="min-h-screen bg-[#f7f7f7]">
      {/* Header */}
      <header className="bg-[#eb6813] py-4 px-4 sm:px-6 md:px-12 lg:px-24 sticky top-0 z-50 shadow-md">
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          <img 
            src={LogoOfertou} 
            onClick={handleClickLogo} 
            alt="Logo Ofertou" 
            className="h-9 cursor-pointer"
          />
          
          <div className="flex items-center gap-4 md:gap-8">
            <a href="/sobre-nos" className="hidden md:block text-white hover:text-[#f0f0f0] transition-colors text-sm md:text-base">
              Sobre nós
            </a>
            
            <button
              type="button"
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-md transition-colors text-sm md:text-base"
              onClick={() => setOpenModalCities(true)}
            >
              <span className="hidden sm:inline">Cidade:</span>
              <span className="font-medium max-w-[120px] truncate">{citySelected}</span>
              <FaChevronDown size={12} />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8 py-6">
        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          <button 
            className={`px-4 py-3 text-sm sm:text-base font-medium relative ${
              activeTab === 'offers' 
                ? 'text-[#eb6813] border-b-2 border-[#eb6813]' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
            onClick={() => changeTab('offers')}
          >
            Ofertas
          </button>
          <button 
            className={`px-4 py-3 text-sm sm:text-base font-medium relative ${
              activeTab === 'partners' 
                ? 'text-[#eb6813] border-b-2 border-[#eb6813]' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
            onClick={() => changeTab('partners')}
          >
            Lojas Parceiras
          </button>
          <button 
            className={`px-4 py-3 text-sm sm:text-base font-medium relative ${
              activeTab === 'mapa' 
                ? 'text-[#eb6813] border-b-2 border-[#eb6813]' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
            onClick={() => changeTab('mapa')}
          >
            Mapa
          </button>
        </div>
        
        {/* Sorting options - Show only in offers and partners tabs */}
        {activeTab !== 'mapa' && (
          <div className="flex items-center mb-6">
            <span className="text-gray-600 text-sm mr-2">Ordenar por:</span>
            <button 
              onClick={handleSortByDistance}
              className="flex items-center gap-2 bg-white text-gray-700 px-3 py-2 rounded-md border border-gray-200 hover:bg-gray-50 shadow-sm text-sm transition-all"
            >
              <FaMapMarkerAlt className="text-[#eb6813]" size={16} />
              <span>Proximidade</span>
            </button>
          </div>
        )}
        
        {/* My location button - Show only in map tab */}
        {activeTab === 'mapa' && (
          <div className="flex items-center mb-6">
            <button 
              onClick={handleSortByDistance}
              className="flex items-center gap-2 bg-white text-gray-700 px-3 py-2 rounded-md border border-gray-200 hover:bg-gray-50 shadow-sm text-sm transition-all"
            >
              <FaMapMarkerAlt className="text-[#eb6813]" size={16} />
              <span>Minha posição</span>
            </button>
          </div>
        )}
        
        {/* Offers Tab Content */}
        {activeTab === 'offers' && (
          <div className="space-y-8">
            {shopsWithOffers.length === 0 ? (
              <div className="bg-white rounded-lg p-8 text-center shadow-sm">
                <ListEmpty message="Não há ofertas disponíveis no momento" />
              </div>
            ) : (
              shopsWithOffers.map((shop: Shop) => {
                const distance = userLocation ? calculateDistance(
                  userLocation.latitude,
                  userLocation.longitude,
                  shop.lat,
                  shop.lon
                ) : null;

                return (
                  <div key={shop.name} id={`shop-${shop.name.replace(/\s+/g, '-').toLowerCase()}`} className="bg-white rounded-lg p-6 shadow-sm">
                    <div className="flex items-start gap-4 mb-4">
                      {shop.shopLogo ? (
                        <img
                          src={shop.shopLogo}
                          alt={`Logo ${shop.name}`}
                          className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-[#eb6813]/10 flex items-center justify-center text-[#eb6813] font-medium flex-shrink-0">
                          {shop.name.charAt(0)}
                        </div>
                      )}
                      
                      <div>
                        <h3 className="font-medium text-lg text-gray-800">{shop.name}</h3>
                        <div className="flex items-center text-gray-500 text-sm mt-1">
                          {distance !== null ? (
                            <div className="flex items-center gap-1">
                              <FaMapMarkerAlt size={12} />
                              <span>{distance.toFixed(1)} km</span>
                            </div>
                          ) : (
                            <span>{shop.location}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <p className="text-sm text-gray-500 mb-3 font-medium">Produtos:</p>
                    
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {shop.offers.map((item, index) => (
                        <ProductCard
                          productImage={item.product.image}
                          key={String(index) + item.product.title}
                          validate={
                            item.endOffer
                              ? `Válido até ${dateFormat(
                                  item.endOffer,
                                )} ou até durarem os estoques`
                              : 'Válido até quando durarem os estoques'
                          }
                          price_old={item.oldPrice}
                          price_new={item.newPrice}
                          title={item.product.title}
                          onClick={() =>
                            handleOpenModalProductSelected({
                              shopName: shop.name,
                              location: shop.location,
                              productTitle: item.product.title,
                              endOffer: item.endOffer || '',
                              newPrice: item.newPrice,
                              oldPrice: item.oldPrice,
                              productImageUrl: item.product.image,
                              whatsapp: shop.whatsapp ? shop.whatsapp : null,
                              instagram: shop.instagram ? shop.instagram : null,
                            })
                          }
                        />
                      ))}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
        
        {/* Partners Tab Content */}
        {activeTab === 'partners' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="text-center mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">Nossos parceiros</h2>
              <p className="text-gray-600 mb-1">Conheça todas as lojas parceiras cadastradas na plataforma</p>
              <p className="text-sm text-gray-500">
                <a href="https://espera.ofertou.app" className="text-[#eb6813] hover:underline">
                  Gostaria de adicionar sua loja aqui?
                </a>
              </p>
            </div>
            
            <div className="space-y-4">
              {allShops.length === 0 ? (
                <div className="text-center py-6">
                  <ListEmpty message="Não há lojas parceiras cadastradas nesta cidade" />
                </div>
              ) : (
                allShops.map((shop: Shop) => {
                  const distance = userLocation ? calculateDistance(
                    userLocation.latitude,
                    userLocation.longitude,
                    shop.lat,
                    shop.lon
                  ) : null;

                  return (
                    <div key={shop.name} className="border border-gray-100 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-center gap-4">
                        {shop.shopLogo ? (
                          <img
                            src={shop.shopLogo}
                            alt={`Logo ${shop.name}`}
                            className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-full bg-[#eb6813]/10 flex items-center justify-center text-[#eb6813] font-semibold text-xl flex-shrink-0">
                            {shop.name.charAt(0)}
                          </div>
                        )}
                        
                        <div className="flex-grow">
                          <h3 className="font-medium text-lg text-gray-800">{shop.name}</h3>
                          <div className="flex items-center text-gray-500 text-sm mt-1">
                            {distance !== null ? (
                              <div className="flex items-center gap-1">
                                <FaMapMarkerAlt size={12} />
                                <span>{distance.toFixed(1)} km</span>
                              </div>
                            ) : (
                              <span>{shop.location}</span>
                            )}
                          </div>
                          
                          <div className="mt-2">
                            {shop.offers.length > 0 ? (
                              <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">
                                Tem ofertas disponíveis
                              </span>
                            ) : (
                              <span className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded-full">
                                Sem ofertas no momento
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex flex-col sm:flex-row gap-2">
                          {shop.whatsapp && (
                            <a 
                              href={`https://wa.me/${shop.whatsapp}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-9 h-9 flex items-center justify-center rounded-full bg-green-500 text-white hover:bg-green-600 transition-colors"
                              title="Contato via WhatsApp"
                            >
                              <FaWhatsapp size={18} />
                            </a>
                          )}
                          
                          {shop.instagram && (
                            <a 
                              href={shop.instagram}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-9 h-9 flex items-center justify-center rounded-full bg-pink-500 text-white hover:bg-pink-600 transition-colors"
                              title="Contato via Instagram"
                            >
                              <BsInstagram size={18} />
                            </a>
                          )}
                        </div>
                      </div>
                      
                      {shop.offers.length > 0 && (
                        <div className="mt-4 text-center sm:text-right">
                          <button 
                            className="px-4 py-2 bg-[#eb6813] text-white text-sm rounded-md hover:bg-[#d55f11] transition-colors"
                            onClick={() => {
                              setActiveTab('offers');
                              setTimeout(() => {
                                const shopElement = document.getElementById(`shop-${shop.name.replace(/\s+/g, '-').toLowerCase()}`);
                                if (shopElement) {
                                  shopElement.scrollIntoView({ behavior: 'smooth' });
                                }
                              }, 100);
                            }}
                          >
                            Ver ofertas
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
        
        {/* Map Tab Content */}
        {activeTab === 'mapa' && (
          <>
            {console.log('Passing to MapView - allShops:', allShops)}
            {console.log('Passing to MapView - shop count:', allShops?.length)}
            {console.log('Passing to MapView - first shop sample:', allShops?.[0])}
            <MapView
              shops={allShops}
              userLocation={userLocation}
              key={userLocation ? `${userLocation.latitude}-${userLocation.longitude}` : 'no-location'}
            />
          </>
        )}
      </main>

      {/* Modals */}
      {openModalSelectedProduct && (
        <ModalProduct setOpenModalSelectedProduct={setOpenModalSelectedProduct}>
          <BodyModalProductSelected
            endOffer={productSelected.endOffer}
            location={productSelected.location}
            newPrice={productSelected.newPrice}
            oldPrice={productSelected.oldPrice}
            productTitle={productSelected.productTitle}
            shopName={productSelected.shopName}
            productImageUrl={productSelected.productImageUrl}
            whatsapp={productSelected.whatsapp}
            instagram={productSelected.instagram}
          />
        </ModalProduct>
      )}
      
      {openModalCities && (
        <CitySelect
          arrayCities={cities}
          handleSelect={handleSelect}
          setOpenModalCities={setOpenModalCities}
        />
      )}

      {/* Footer */}
      <Footer />
      
      <ToastContainer />
    </div>
  );
};

export default App;
